import { Outlet, useLocation, useNavigate } from 'react-router'

import { SignalLogProvider, useSignalLog } from '@/app/lib/signalLog'
import { ErrorBoundary } from '@/ui/ErrorBoundary'
import { SignalTerminal } from '@/ui/SignalTerminal'
import { TopNav, type TopNavItem } from '@/ui/TopNav'

/** The one hue of the whole product (spec 004 D1): echo's Quant teal, on the bar, header and screens alike. */
const HUE = 193

const NAV: { label: string; href: string }[] = [
  { label: 'Strategies', href: '/strategies' },
  { label: 'Manual', href: '/manual' },
]

/** Layer 1: the station bar with the wordmark and the two destinations. */
function ShellNav() {
  const location = useLocation()
  const navigate = useNavigate()
  const items: TopNavItem[] = NAV.map((entry) => ({
    ...entry,
    active: location.pathname.startsWith(entry.href),
    onClick: (event) => {
      event.preventDefault()
      void navigate(entry.href)
    },
  }))
  return <TopNav items={items} wordmark="QUANT MISSION CONTROL" hue={HUE} lastRight={false} />
}

/** The terminal band, wired to the signal-log store (echo's StationShell mount). */
function TerminalMount() {
  const { logs, open, toggleOpen, cmd, setCmd, submitCommand } = useSignalLog()
  return (
    <SignalTerminal
      logs={logs}
      open={open}
      onToggle={toggleOpen}
      cmd={cmd}
      onCmdChange={setCmd}
      onCommand={submitCommand}
      className="shrink-0"
    />
  )
}

/**
 * The frame around every screen: station bar, Quant header, the routed screen
 * and the signal terminal. One fixed-position column, no page scroll.
 */
export function Shell() {
  const location = useLocation()
  return (
    <SignalLogProvider operator="QMC">
      <div
        data-quant-root
        style={{ '--h': HUE } as React.CSSProperties}
        className="fixed inset-0 flex flex-col bg-ink text-paper"
      >
        <a
          href="#main"
          className="sr-only z-30 bg-paper px-3 py-1.5 text-ink focus:not-sr-only focus:absolute focus:left-2 focus:top-2"
        >
          Skip to content
        </a>
        <ShellNav />
        <main id="main" tabIndex={-1} className="flex min-h-0 flex-1 flex-col overflow-y-auto focus:outline-none">
          <ErrorBoundary label="This page" resetKeys={[location.pathname]}>
            <Outlet />
          </ErrorBoundary>
        </main>
        <ErrorBoundary label="Terminal" className="shrink-0 p-2 font-mono text-[11px] text-paper-50">
          <TerminalMount />
        </ErrorBoundary>
      </div>
    </SignalLogProvider>
  )
}
